<script>
import api from '@/helpers/api'
import { recentOrders, withDevList } from './dev-data'

export default {
  name: 'latest-orders-cards',
  props: {
    length: { type: Number, default: 5 }
  },
  data () {
    return {
      isLoading: false,
      data: []
    }
  },
  mounted () {
    this.onRefresh()
  },
  methods: {
    async onRefresh () {
      this.isLoading = true

      let items = []
      try {
        const result = await api.webForms.fetchPage({
          page: 1,
          pageSize: this.length,
          filters: [{ is_submitted: { _eq: true } }],
          orderBy: { submitted_at: 'desc' }
        })
        items = result.items || []
      } catch (e) {
        items = []
      }

      this.data = withDevList(items, recentOrders, this.length)
      this.isLoading = false
    },
    openOrder (item) {
      if (String(item.id).startsWith('dev-')) return
      this.$router.push({
        name: 'sales.my-orders.view',
        params: { id: item.id }
      })
    },
    referenceOf (item) {
      return item.reference || this.$t('no reference')
    },
    itemOf (item) {
      return (
        (item.item && item.item.short_description) || this.$t('my_orders.dash')
      )
    },
    modelOf (item) {
      if (!item.form_data || !item.form_data.model_type) return null
      return this.$t(`attribute.model_type.${item.form_data.model_type}`)
    },
    foamOf (item) {
      if (!item.form_data || !item.form_data.foam_type) return null
      return this.$t(`attribute.foam_type_short.${item.form_data.foam_type}`)
    },
    isMixFoam (item) {
      return !!(item.form_data && item.form_data.foam_type === 'mix')
    },
    shippingOf (item) {
      if (item.shipping_address) {
        return `${item.shipping_address.company_name}, ${item.shipping_address.city}`
      }
      return this.$t('no shipping')
    },
    quantityOf (item) {
      return item.quantity != null ? item.quantity : this.$t('my_orders.dash')
    }
  }
}
</script>

<template>
  <div class="portal-card portal-card--panel h-100">
    <div class="d-flex align-items-center justify-content-between mb-3">
      <div>
        <div class="portal-card__eyebrow mb-1">
          {{ $t('dashboard.recent_orders.title') }}
        </div>
        <div class="portal-card__panel-title">
          {{ $t('dashboard.recent_orders.subtitle', { length }) }}
        </div>
      </div>
      <div class="d-flex align-items-center">
        <b-btn variant="link" size="sm" class="p-0 mr-3" @click="onRefresh">
          <i class="fas fa-sync"></i>
        </b-btn>
        <b-btn
          variant="link"
          size="sm"
          class="p-0"
          :to="{ name: 'sales.my-orders' }"
        >
          {{ $t('dashboard.recent_orders.view_all') }}
        </b-btn>
      </div>
    </div>

    <div v-if="isLoading" class="text-muted small py-3">
      {{ $t('Loading...') }}
    </div>

    <div v-else-if="!data.length" class="text-muted small py-3">
      {{ $t('dashboard.recent_orders.empty') }}
    </div>

    <div v-else class="order-table-wrap">
      <div class="order-table" role="table">
        <div class="order-table__head" role="row">
          <div class="order-table__col order-table__col--date" role="columnheader">
            {{ $t('my_orders.col.date') }}
          </div>
          <div
            class="order-table__col order-table__col--reference"
            role="columnheader"
          >
            {{ $t('my_orders.col.reference') }}
          </div>
          <div class="order-table__col order-table__col--item" role="columnheader">
            {{ $t('my_orders.col.item') }}
          </div>
          <div
            class="order-table__col order-table__col--shipping"
            role="columnheader"
          >
            {{ $t('my_orders.col.shipping') }}
          </div>
          <div class="order-table__col order-table__col--qty" role="columnheader">
            {{ $t('my_orders.col.qty') }}
          </div>
          <div
            class="order-table__col order-table__col--status"
            role="columnheader"
          >
            {{ $t('my_orders.col.status') }}
          </div>
          <div class="order-table__col order-table__col--action" role="columnheader"></div>
        </div>

        <button
          v-for="item in data"
          :key="item.id"
          type="button"
          class="order-table__row"
          role="row"
          @click="openOrder(item)"
        >
          <div class="order-table__col order-table__col--date" role="cell">
            <span class="order-table__primary">
              {{ item.submitted_at | moment('DD MMM YYYY') }}
            </span>
          </div>

          <div class="order-table__col order-table__col--reference" role="cell">
            <div class="order-table__ref">
              <span class="order-table__primary">{{ referenceOf(item) }}</span>
              <span
                v-if="item.form_data && item.form_data.is_remill"
                class="order-table__flag"
                :title="$t('Remill')"
              >
                <i class="mdi mdi-alpha-r-box"></i>
              </span>
              <span
                v-if="item.file"
                class="order-table__flag"
                :title="item.file.file_name"
              >
                <i class="mdi mdi-file-check-outline"></i>
              </span>
            </div>
          </div>

          <div class="order-table__col order-table__col--item" role="cell">
            <div class="order-table__item">
              <span class="order-table__primary">{{ itemOf(item) }}</span>
              <div class="order-table__item-meta">
                <span v-if="modelOf(item)" class="order-table__muted">{{
                  modelOf(item)
                }}</span>
                <span
                  v-if="foamOf(item)"
                  :id="`ttRecentFoamType${item.id}`"
                  class="order-table__foam"
                  :class="{ 'order-table__foam--mix': isMixFoam(item) }"
                >{{ foamOf(item) }}</span>
                <b-tooltip
                  v-if="isMixFoam(item)"
                  :target="`ttRecentFoamType${item.id}`"
                  variant="dark"
                  placement="top"
                  container="body"
                  custom-class="text-left"
                >
                  <p class="mx-2 my-0">
                    {{ $t('Seat') }}:
                    {{
                      $t(
                        `attribute.foam_type.${item.form_data.mix_seat_foam_type}`
                      )
                    }}<br />
                    {{ $t('Back') }}:
                    {{
                      $t(
                        `attribute.foam_type.${item.form_data.mix_back_foam_type}`
                      )
                    }}
                  </p>
                </b-tooltip>
              </div>
            </div>
          </div>

          <div class="order-table__col order-table__col--shipping" role="cell">
            <span class="order-table__muted">{{ shippingOf(item) }}</span>
          </div>

          <div class="order-table__col order-table__col--qty" role="cell">
            <span class="order-table__primary">{{ quantityOf(item) }}</span>
          </div>

          <div class="order-table__col order-table__col--status" role="cell">
            <app-status-badge :status="item.status" />
          </div>

          <div class="order-table__col order-table__col--action" role="cell">
            <span class="order-table__action">
              <i class="mdi mdi-arrow-right"></i>
            </span>
          </div>
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.portal-card--panel {
  background: #fff;
  border-radius: 1rem;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.04);
  padding: 1.15rem 1.25rem;
}

.portal-card__eyebrow {
  color: #94a3b8;
  font-size: 0.8rem;
  font-weight: 500;
}

.portal-card__panel-title {
  color: #0f172a;
  font-size: 1.05rem;
  font-weight: 600;
}

.order-table-wrap {
  overflow-x: auto;
  margin: 0 -0.15rem;
}

.order-table {
  min-width: 720px;
}

.order-table__head,
.order-table__row {
  display: grid;
  grid-template-columns:
    6.75rem
    minmax(7.5rem, 1fr)
    minmax(10rem, 1.6fr)
    minmax(7rem, 1.1fr)
    2.75rem
    8rem
    2.25rem;
  gap: 0.65rem;
  align-items: center;
  padding: 0.7rem 0.15rem;
}

.order-table__head {
  border-bottom: 1px solid #eef2f7;
  color: #94a3b8;
  font-size: 0.72rem;
  font-weight: 500;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.order-table__row {
  appearance: none;
  width: 100%;
  border: 0;
  border-bottom: 1px solid #eef2f7;
  background: transparent;
  text-align: left;
  cursor: pointer;

  &:last-child {
    border-bottom: 0;
  }

  &:hover {
    .order-table__primary {
      color: #556ee6;
    }
  }
}

.order-table__primary {
  color: #0f172a;
  font-size: 0.88rem;
  font-weight: 500;
}

.order-table__muted {
  color: #64748b;
  font-size: 0.84rem;
}

.order-table__foam {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 3.25rem;
  padding: 0.15rem 0.4rem;
  border-radius: 0.4rem;
  background: #f1f5f9;
  color: #0f172a;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.order-table__foam--mix {
  cursor: help;
}

.order-table__item {
  min-width: 0;
}

.order-table__item-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.2rem;
}

.order-table__ref {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  min-width: 0;
}

.order-table__flag {
  color: #556ee6;
  font-size: 1rem;
  line-height: 1;
  flex: 0 0 auto;
}

.order-table__col--qty {
  text-align: center;
}

.order-table__col--status,
.order-table__col--action {
  display: flex;
  justify-content: flex-end;
}

.order-table__action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.85rem;
  height: 1.85rem;
  border-radius: 0.5rem;
  background: #f1f5f9;
  color: #0f172a;
}
</style>
